import { Modal, Typography } from 'antd';
import { FC, PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { getAppBasename, isAuthenticationUrl, peekSharedUserManager, setPostLoginUrl } from '../configs/auth-config';
import { matchesAnyPath } from '../utils/paths';
import {
  acknowledgeSessionDead,
  installSessionRecoveryTriggers,
  registerAuthErrorSource,
  registerSessionRenewer,
  setOnSessionDead,
  startSessionRecovery,
  stopSessionRecovery,
  suspendSessionRecovery
} from '../configs/session-recovery';

type SessionGuardProps = PropsWithChildren<{
  /**
   * Rutas de esta aplicacion, **sin el basename**, donde el aviso es ruido y no se pinta.
   *
   * Las tres aplicaciones montan esto envolviendo el router entero, asi que por defecto el aviso sale
   * en cualquier pantalla, incluidas las publicas y las de entrar (j2l-shared#124). Las pantallas de
   * autenticacion no hace falta listarlas aqui: esas van en `getOidcConfig({ authPaths })`, porque
   * `setPostLoginUrl` necesita conocerlas tambien.
   *
   * Solo se silencia el aviso. La renovacion sigue en pie: quien navegue por una pagina publica con la
   * sesion viva la sigue renovando, y sus llamadas autenticadas se siguen recuperando de un 401.
   */
  quietPaths?: string[];
}>;

/**
 * Mantiene viva la renovacion del token, y avisa cuando ya no hay nada que mantener.
 *
 * Va DENTRO de `AuthProvider`, porque lo unico que necesita de el es su `signinSilent`, sus eventos
 * y su `auth.error`. Registrar ese `signinSilent` en `session-recovery` es lo que permite al
 * interceptor de axios renovar sin construir un segundo `UserManager` sobre el mismo `userStore`.
 *
 * El modal no redirige solo, a proposito. El fallo que arregla esto le llega al usuario con un
 * formulario largo a medias -- asi es como se reporto, un wizard de varios pasos que al terminar
 * daba error -- asi que se le dice lo que pasa, se le deja cerrar el aviso para copiar lo que
 * necesite, y solo se navega cuando el pulsa. El texto dice que al volver a entrar se recarga la
 * pagina, porque `signinRedirect` es una navegacion completa y prometer lo contrario seria una
 * trampa.
 */
const SessionGuard: FC<SessionGuardProps> = ({ children, quietPaths }) => {
  const auth = useAuth();
  const { t } = useTranslation();
  const [sessionDead, setSessionDead] = useState(false);
  const [redirecting, setRedirecting] = useState(false);

  // Dentro del iframe de renovacion silenciosa no se vigila nada. Si el corto-circuito del
  // `index.html` no llegase a actuar, la SPA arranca ahi dentro y sin esta guarda montaria su propia
  // recuperacion: una renovacion dentro de la renovacion. Es la misma razon por la que
  // `auth-config.shouldSkipCheck` empieza comprobando esto.
  const inSilentRenewFrame = typeof window !== 'undefined' && window.self !== window.top;

  /**
   * Hay a quien renovarle la sesion en cuanto hay un usuario guardado. **No** se mira
   * `auth.isAuthenticated`: los dos reductores de `react-oidc-context` en juego lo calculan como
   * `action.user ? !action.user.expired : false`, o sea que alguien que vuelve al dia siguiente --
   * el caso exacto de este arreglo -- arranca con `user` y con `isAuthenticated` en `false`, y
   * exigirlo le habria dejado sin renovador, sin suscripciones y sin aviso.
   *
   * El visitante anonimo, que es de quien protege esta guarda, no tiene `user` ninguno.
   */
  const hasSession = auth.user != null;

  /**
   * `auth` NO puede estar en las dependencias del efecto de abajo, y esto no es una optimizacion.
   *
   * `react-oidc-context` construye un objeto `auth` nuevo en cada cambio de estado, y despacha
   * estado en `NAVIGATOR_INIT`, `NAVIGATOR_CLOSE`, `USER_LOADED` y `ERROR`. Como `ERROR` es
   * justamente lo que despacha al llegar un `silentRenewError`, con `auth` en las dependencias el
   * efecto se limpiaba en el mismo tick en que el manejador acababa de llamar a
   * `startSessionRecovery`, y su `stopSessionRecovery` mataba la recuperacion recien nacida. Se veia
   * en local: el fallo no reintentaba a los 5 s, y la sesion solo se salvaba 48 s despues, cuando
   * `accessTokenExpired` volvia a arrancarla por otra via.
   */
  const authRef = useRef(auth);
  authRef.current = auth;

  /**
   * El ultimo error de renovacion, capturado del evento y no del estado renderizado.
   *
   * `auth.error` llega un ciclo tarde: React despacha por el Scheduler, que es un macrotask, y la
   * clasificacion de `session-recovery` corre en el microtask de la promesa. Con este `ref` la
   * primera renovacion fallida ya se clasifica bien; `auth.error` se queda de respaldo.
   */
  const lastRenewErrorRef = useRef<unknown>(undefined);

  /**
   * El ultimo error que ya decidio una clasificacion. Un error clasifica UNA vez.
   *
   * Ni `lastRenewErrorRef` ni `auth.error` se limpiaban salvo al cargar un usuario, asi que un
   * `login_required` de hace rato seguia decidiendo: durante un despliegue de identity la renovacion
   * falla por corte de conexion, que es pasajero y reintentable, y ese error viejo lo convertia en
   * «sesion muerta». El aviso salia por algo que ya habia pasado (j2l-shared#124).
   *
   * No se acota al intento en curso, que seria lo obvio, porque romperia el caso para el que existe el
   * respaldo: el `signinSilent` de `react-oidc-context` se traga el error y devuelve `null` -- desde la
   * 2.3.0 --, y el `login_required` que clasifica ese `null` llego por `silentRenewError` **antes** de
   * que el intento arrancara. Con un corte por
   * marca de tiempo, por esa via no se detectaria jamas una sesion muerta -- justo lo que advierte el
   * comentario de `session-recovery`.
   */
  const consumedAuthErrorRef = useRef<unknown>(undefined);

  useEffect(() => {
    if (inSilentRenewFrame || !hasSession) return;

    /**
     * Se renueva por el `UserManager` DE VERDAD, no por `auth.signinSilent`.
     *
     * El envoltorio de `react-oidc-context` despacha `NAVIGATOR_INIT` antes de esperar, o sea que
     * pone `activeNavigator: 'signinSilent'`, y los layouts de las tres aplicaciones reaccionan a
     * ese estado pintando `.global-spinner`: un velo fijo de 100vh por 100vw con «Refrescando
     * credenciales». `SilentRenewService` llama al manager directamente, y por eso una renovacion
     * automatica no ha tapado nunca la pantalla. Por el envoltorio, cada reintento de fondo y cada
     * 401 recuperado apagarian la pagina encima del formulario que esto viene a proteger.
     *
     * El respaldo existe por si alguna aplicacion no llegase a pasar el manager a `AuthProvider`:
     * antes renovar con velo que no renovar.
     */
    registerSessionRenewer(() => {
      const manager = peekSharedUserManager();
      return manager != null ? manager.signinSilent() : authRef.current.signinSilent();
    });
    registerAuthErrorSource(() => {
      const candidate = lastRenewErrorRef.current ?? authRef.current.error;
      if (candidate == null || candidate === consumedAuthErrorRef.current) return undefined;
      consumedAuthErrorRef.current = candidate;
      lastRenewErrorRef.current = undefined;
      return candidate;
    });

    return () => {
      registerSessionRenewer(null);
      registerAuthErrorSource(null);
      // Sin esto el temporizador sigue vivo con el renovador ya retirado, avisando por consola cada
      // cinco minutos hasta el final de la vida de la pagina.
      //
      // `suspend` y no `stop`: este efecto depende de `hasSession`, y `stopSessionRecovery` olvida
      // ademas que la sesion esta muerta. Un parpadeo de `auth.user` a `null` borraba ese estado sin
      // que hubiera llegado ningun usuario nuevo, y el aviso se abria o se cerraba por un cambio de
      // estado de React en vez de por lo que hubiera pasado con la sesion.
      suspendSessionRecovery();
    };
  }, [hasSession, inSilentRenewFrame]);

  /**
   * Por la misma razon que `authRef`: las aplicaciones pasan la lista en linea, o sea un array nuevo en
   * cada render, y con el en las dependencias el efecto de abajo se daria de baja y de alta sin parar.
   */
  const quietPathsRef = useRef(quietPaths);
  quietPathsRef.current = quietPaths;

  /**
   * Si en la pantalla de ahora mismo el aviso hace mas dano que bien.
   *
   * Se lee `window.location` y no el router porque las tres aplicaciones montan esto **envolviendo el
   * router entero**: aqui dentro no hay `useLocation`, y ese es tambien el motivo de que el fallo
   * exista.
   */
  const warningWouldBeNoise = useCallback(
    () => isAuthenticationUrl() || matchesAnyPath(window.location.pathname, quietPathsRef.current, getAppBasename()),
    []
  );

  /**
   * La puerta va aqui, en el aviso, y no en el render.
   *
   * Es el unico punto por el que pasan los dos caminos que abren el modal -- el temporizador de
   * `session-recovery` y el `notifySessionDead()` directo del interceptor de axios -- y ademas este
   * componente envuelve al router, asi que no se re-renderiza al navegar: una guarda en el render
   * estaria mirando la ruta de hace rato.
   */
  useEffect(() => {
    if (inSilentRenewFrame) return;
    setOnSessionDead(() => {
      if (warningWouldBeNoise()) {
        // La sesion sigue muerta; lo que no ayuda es decirselo aqui. Se baja la marca para que el
        // siguiente 401, ya en otra pantalla, vuelva a avisar. Es la misma semantica que «Ahora no», y
        // sin ella el aviso se perderia para el resto de la vida de la pestana.
        acknowledgeSessionDead();
        return;
      }
      setSessionDead(true);
    });
    return () => setOnSessionDead(null);
  }, [inSilentRenewFrame, warningWouldBeNoise]);

  useEffect(() => {
    if (inSilentRenewFrame) return;
    return installSessionRecoveryTriggers();
  }, [inSilentRenewFrame]);

  // `addSilentRenewError` devuelve su propia funcion de baja, que es lo que el efecto retorna.
  useEffect(() => {
    if (inSilentRenewFrame || !hasSession) return;
    return auth.events.addSilentRenewError((error: unknown) => {
      lastRenewErrorRef.current = error;
      startSessionRecovery();
    });
  }, [auth.events, hasSession, inSilentRenewFrame]);

  useEffect(() => {
    if (inSilentRenewFrame || !hasSession) return;
    // Solo se arranca la recuperacion, sin forzar un intento inmediato. `SilentRenewService` puede
    // tener todavia su propia renovacion en vuelo -- `silentRequestTimeoutInSeconds` acota la
    // navegacion del iframe, no el POST de token ni el GET de userinfo -- y lanzar otra encima
    // romperia el «un solo `signinSilent` a la vez sobre el mismo `userStore`» que `auth-config`
    // enuncia. La primera pausa del calendario son 5 s, que es espera de sobra.
    return auth.events.addAccessTokenExpired(() => startSessionRecovery());
  }, [auth.events, hasSession, inSilentRenewFrame]);

  useEffect(() => {
    if (inSilentRenewFrame) return;
    return auth.events.addUserLoaded(() => {
      lastRenewErrorRef.current = undefined;
      consumedAuthErrorRef.current = undefined;
      stopSessionRecovery();
      setSessionDead(false);
      setRedirecting(false);
    });
  }, [auth.events, inSilentRenewFrame]);

  // El envoltorio de `signinRedirect` atrapa el error y devuelve `null` en vez de rechazar, y lo hace
  // desde `react-oidc-context` 2.3.0, asi que el `catch` de abajo no basta para desbloquear el boton:
  // lo que si llega es un `auth.error` nuevo.
  useEffect(() => {
    if (auth.error != null) setRedirecting(false);
  }, [auth.error]);

  const goToLogin = useCallback(async () => {
    setRedirecting(true);
    // Sin argumento: la URL actual, que es donde el usuario quiere volver. `setPostLoginUrl` se niega
    // sola si esa URL es una pantalla de autenticacion, y por eso aqui no hay que comprobar nada.
    setPostLoginUrl();
    try {
      await auth.signinRedirect();
    } catch {
      setRedirecting(false);
    }
  }, [auth]);

  const dismiss = useCallback(() => {
    // La sesion sigue muerta: esto solo cierra el aviso para que el usuario pueda copiar lo suyo. El
    // siguiente 401 se lo vuelve a poner delante.
    acknowledgeSessionDead();
    setSessionDead(false);
  }, []);

  if (inSilentRenewFrame) return <>{children}</>;

  return (
    <>
      {children}
      <Modal
        open={sessionDead}
        title={t('Tu sesión ha caducado')}
        onCancel={dismiss}
        onOk={goToLogin}
        confirmLoading={redirecting}
        // Se cierra con Escape y con el boton, pero no con un clic fuera: el aviso llega encima de un
        // formulario a medias y un clic perdido no puede hacerlo desaparecer.
        //
        // `mask.closable` y no `maskClosable`. El suelo real es **antd 6.3**, no la mayor 6 a secas:
        // en la 6.2.0 `MaskConfig` solo declara `enabled` y `blur`, y `maskClosable` todavia no esta
        // marcada obsoleta; `closable` y esa marca entran en la 6.3.0 (comprobado en los tipos de las
        // dos). Antes de eso, antd 5 declaraba `mask?: boolean` a secas, sin forma de objeto
        // (comprobado en la 5.29.3, la ultima 5.x). Ninguna de las tres aplicaciones declara por debajo
        // de antd 6.3 en `main`, medido el 2026-10-05. Sin la cifra de cada una a proposito: las
        // versiones solo suben, asi que el suelo se sostiene, mientras que un trio de numeros caduca en
        // la siguiente subida de rutina y este fichero se copia a las tres apps (j2l-shared#127).
        // Un `mask` sin `enabled` deja el velo puesto: lo que se quita es el cierre por clic fuera,
        // no el velo.
        mask={{ closable: false }}
        okText={t('Volver a entrar')}
        cancelText={t('Ahora no')}
        okButtonProps={{ shape: 'round', type: 'primary' }}
        cancelButtonProps={{ shape: 'round' }}
      >
        <Typography.Paragraph>
          {t(
            'Has estado mucho rato sin actividad y no hemos podido renovar la sesión. Para seguir tienes que volver a entrar, y al hacerlo se recargará la página: copia antes lo que no quieras perder.'
          )}
        </Typography.Paragraph>
      </Modal>
    </>
  );
};

export default SessionGuard;
